import { useForm }                                 from 'react-hook-form';
import { useTranslation }                          from 'react-i18next';
import React                                       from 'react';
import StandModel                                  from '@/models/services/website/StandModel';
import StandPageModel                              from '@/models/services/website/StandPageModel';
import StandPageValidationStatusModel              from '@/models/services/website/StandPageValidationStatusModel';
import StandPageValidationStatusHistoryModel       from '@/models/services/website/StandPageValidationStatusHistoryModel';
import { toaster }                                 from '@/components/ui/toaster';
import { useStandPageEditors }                     from './useStandPageEditors';
import { useStandPageLinks }                       from './useStandPageLinks';
import { useStandPageFiles }                       from './useStandPageFiles';
import { getStandPageStatusReference }             from './standPageSelection';

export type { FormValues, SocialNetworkRow, ModelFile } from './standFormTypes';
import type { FormValues }                         from './standFormTypes';

interface EditStandFormInput {
	stand: StandModel;
	standPage: StandPageModel | null;
	validationStatuses: StandPageValidationStatusModel[];
}

export const useEditStandForm = (data: EditStandFormInput) => {
	const { stand, standPage: initialStandPage, validationStatuses } = data;
	const { t } = useTranslation();

	// ─── État du standPage ───────────────────────────────────────────────────────

	const [activeStandPage, setActiveStandPage] = React.useState<StandPageModel | null>(initialStandPage ?? null);

	// Détermine le comportement du formulaire selon le statut courant du standPage.
	// On résout la référence depuis la liste déjà chargée (pas de fetch de relation).
	const activeStatusReference  = getStandPageStatusReference(activeStandPage, validationStatuses);
	const isFormDisabled         = activeStatusReference === 'VALIDATION_REQUESTED';
	// On ne crée une nouvelle version que si le seul standPage disponible est déjà Validé.
	// En création / À valider / Refusé → on modifie le standPage existant.
	const willCreateNewStandPage = activeStatusReference === 'VALIDATED';

	// ─── Statut de validation ────────────────────────────────────────────────────

	const validationRequestedStatus = validationStatuses.find(m => m.reference === 'VALIDATION_REQUESTED');

	// ─── Formulaire ─────────────────────────────────────────────────────────────

	const { control, handleSubmit, register, setValue, watch, formState } = useForm<FormValues>({
		mode: 'onChange',
		defaultValues: {
			socialNetworks:      [{ linkTypeUrn: '', url: '' }],
			logo:                null,
			gallery:             [],
			existingLogoUrl:     activeStandPage?.getFiles('stand_page_logo').at(0)?.url ?? null,
			existingGalleryUrls: activeStandPage?.getFiles('stand_page_image').slice(0, 3).map(f => f.url) ?? [],
		},
	});

	// ─── Sous-hooks ─────────────────────────────────────────────────────────────

	const { editors, getTranslations } = useStandPageEditors(activeStandPage);

	const { linkTypes, linkTypesLoading, standPageLinksLoading, socialNetworksField, saveStandPageLinks } =
		useStandPageLinks(activeStandPage, control);

	const { typeCollectionLoading, existingLogoRef, existingGalleryRef, saveFiles, refreshFormFiles } =
		useStandPageFiles(stand, activeStandPage, setValue, setActiveStandPage, validationStatuses);

	// ─── Sauvegarde du standPage ─────────────────────────────────────────────────

	const saveStandPage = async (needsNewStandPage: boolean) => {
		const currentStandPage = needsNewStandPage ? new StandPageModel() : (activeStandPage as StandPageModel);
		await currentStandPage.patch({
			stand:        stand.urn,
			translations: getTranslations(),
		});
		return currentStandPage;
	};

	// ─── Workflow de soumission ──────────────────────────────────────────────────

	const save = async (formData: FormValues, mode: 'submit' | 'draft') => {
		const needsNewStandPage = !activeStandPage || willCreateNewStandPage;

		// 1 - Créer ou mettre à jour le standPage (textes & traductions)
		const currentStandPage = await saveStandPage(needsNewStandPage);

		// 2 - Sauvegarder les liens sociaux et les fichiers (opérations indépendantes en parallèle)
		await Promise.all([
			saveStandPageLinks(formData.socialNetworks, currentStandPage, needsNewStandPage),
			saveFiles(formData, currentStandPage, existingLogoRef.current, existingGalleryRef.current, needsNewStandPage),
		]);

		// 3 - (Mode "soumettre" uniquement) Passer le statut à VALIDATION_REQUESTED
		if (mode === 'submit') {
			await new StandPageValidationStatusHistoryModel().patch({
				standPage:                    currentStandPage.urn,
				standPageValidationStatus:    validationRequestedStatus?.urn,
			});
		}

		// 4 - Rafraîchir le formulaire avec les données du standPage sauvegardé
		await refreshFormFiles();
	};

	const createSaveHandler = (mode: 'submit' | 'draft', successKey: string) =>
		handleSubmit(async (formData) => {
			try {
				await save(formData, mode);
				toaster.create({ title: t(successKey), type: 'success' });
			} catch (error) {
				console.error(error);
				toaster.create({ title: t('pages.editStand.form.messages.error'), type: 'error' });
			}
		});

	const onSubmit = createSaveHandler('submit', 'pages.editStand.form.messages.submitSuccess');
	const onDraft  = createSaveHandler('draft',  'pages.editStand.form.messages.draftSuccess');

	return {
		linkTypes,
		isLoading: linkTypesLoading || standPageLinksLoading || typeCollectionLoading,
		isFormDisabled,
		willCreateNewStandPage,
		register,
		editors,
		control,
		setValue,
		watch,
		formState,
		socialNetworksField,
		standTitle: stand.title,
		onSubmit,
		onDraft,
	};
};
